<template>
  <div v-if="showModal" class="overlay" @click.self="handleOverlayClick">
    <div
      class="modal nunito-sans-light bg-white p-4 md:p-12 shadow-md w-full md:w-11/12 max-w-4xl mx-auto relative overflow-y-auto max-h-screen">
      <button class="absolute top-2 right-3 text-gray-500 hover:text-gray-800" @click="handleOverlayClick">
        <img src="@/assets/cross.svg" alt="Close" class="w-4 h-4 mt-2 mr-2" />
      </button>

      <!-- Loading Spinner -->
      <div v-if="isLoadingProduct">
        <LoadingSpinner />
      </div>

      <!-- Product Details Section -->
      <div v-else-if="product" class="flex flex-col md:flex-row gap-6">
        <!-- Gallery Section -->
        <div class="w-full md:w-1/2">
          <!-- Main Image Display -->
          <div>
            <img
              :src="selectedImage && selectedImage.path ? `${$baseURL}/${selectedImage.path}` : require('@/assets/Mark 1.jpg')"
              alt="Product Image" class="w-full object-cover border border-grey mb-4" />
          </div>

          <!-- Thumbnail Gallery -->
          <div class="flex space-x-2 md:space-x-4 overflow-x-auto">
            <div v-for="(image, index) in product.images" :key="index" class="cursor-pointer">
              <img :src="image.path ? `${$baseURL}/${image.path}` : require('@/assets/Mark 1.jpg')"
                alt="Product Thumbnail" class="w-16 h-16 md:w-24 md:h-24 object-cover border"
                :class="{ 'border-[#8a2432]': selectedImage === image, 'border-grey': selectedImage !== image }"
                @click="selectImage(image)" />
            </div>
          </div>
        </div>

        <!-- Product Info Section -->
        <div class="w-full md:w-1/2 space-y-4">
          <div class="flex justify-between items-center mt-2 text-gray-500 text-sm">
            <p>Lot #{{ product.lot_number }}</p>
          </div>

          <h1 class="text-lg md:text-xl text-gray-800 raleway-custom ">{{ product.lot_title }}</h1>
          <!-- Pricing Section -->
          <div class="flex flex-col md:flex-row">
            <div class="flex-1 text-center border p-4">
              <p class="text-gray-500 text-sm">Starting Bid:</p>
              <p class="text-gray-800 text-lg font-bold">${{ product.starting_bid || '0.00' }}</p>
            </div>
            <div class="flex-1 text-center border-t border-b md:border-r p-4">
              <p class="text-[#8a2432] text-sm">Current Bid:</p>
              <p class="text-[#8a2432] text-lg font-bold">
                <span v-if="isLoadingBid">--</span>
                <span v-else>${{ currentBid }}</span>
              </p>
            </div>
          </div>
          <div v-if="minimumBid !== null" class="flex">
            <p class="text-[#000] text-sm pr-2">Minimum Acceptable Bid</p>
            <p class="text-[#8a2432] text-sm">
              <span v-if="isLoadingBid">Loading...</span>
              <span v-else>${{ minimumBid }}</span>
            </p>
          </div>

          <!-- Messages -->
          <div v-if="message" class="flex items-center space-x-2 relative">
            <!-- Success Message -->
            <p v-if="message.type === 'success'"
              class="flex items-center w-full text-[#28A34B] text-[12px] border-t bg-[#f0fff5] border-b border-l-2 p-2 border-[#28A34B] border-r">
              {{ message.text }}
            </p>

            <!-- Info Message -->
            <p v-else-if="message.type === 'info'"
              class="flex items-center w-full text-[#FFA500] text-[12px] border-t bg-[#EDF5FF] border-b border-l-2 p-2 border-[#FFA500] border-r">
              {{ message.text }}
            </p>

            <!-- Error Message -->
            <p v-else-if="message.type === 'error'"
              class="flex items-center w-full text-[#DA1E28] text-[12px] border-t bg-[#FFF1F1] border-b border-l-2 p-2 border-[#DA1E28] border-r">
              {{ message.text }}
            </p>
            <!-- Close Button -->
            <button @click="message = null"
              class="absolute right-2 top-1.5 text-gray-400 hover:text-gray-700 focus:outline-none"
              aria-label="Close message">
              <img src="@/assets/cross.svg" alt="Close" class="w-3 mt-1.5 h-3" />
            </button>
          </div>



          <div class="p-6 bg-red-100">
            <div class="flex flex-col sm:flex-row sm:items-center relative">
              <!-- Dollar Sign inside the input field -->
              <div class="relative flex-grow">
                <span class="absolute left-4 top-1/2 transform -translate-y-1/2 text-black font-bold">$</span>
                <!-- Input Field for Bid Amount -->
                <input type="text" v-model="bidAmount" placeholder="Enter Bidding Amount"
                  class="w-full pl-8  px-4 py-2 focus:outline-none focus:ring focus:ring-red-300" />
              </div>
              <!-- Bid Now Button -->
              <button
                class="bg-[#8a2432] text-white px-4 py-2 hover:bg-gray-900 focus:outline-none focus:ring focus:ring-red-400 sm:w-36 sm:mt-0 mt-2 w-full w-auto"
                @click="submitBid">
                Bid Now
              </button>
            </div>
          </div>

          <!-- Display myStatus message -->
          <div v-if="myStatus" :class="{
            'text-[#28A34B] border-[#28A34B] bg-[#f0fff5]/10': winStatus === 'winning',
            'text-[#DA1E28] border-[#DA1E28] bg-[#FFF1F1]/10': winStatus !== 'winning'
          }" class="mt-4 text-sm border py-2 px-4" v-html="myStatus">
          </div>
          <!-- View Details Button -->
          <div class="mt-4" @click.self="handleOverlayClick">
            <button class="text-[#8a2432] font-medium" @click="redirectToProductPage">
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Bid Confirmation Modal -->
    <BidConfirmation
      v-if="showBidConfirmation && bidConfirmItem"
      :title="bidConfirmItem.lot_title"
      :bidAmount="parseFloat(bidConfirmItem.bidAmount)"
      :slug="bidConfirmItem.slug"
      @confirm="confirmBid"
      @decline="declineBid"
    />
  </div>
</template>

<script>
import apiClient from '@/axios'; // Import the axios instance
import Cookies from 'js-cookie'; // Import js-cookie
import LoadingSpinner from '@/components/LoadingSpinner.vue'; // Import LoadingSpinner
import BidConfirmation from '@/components/BidConfirmation.vue'; // Import BidConfirmation
import { initializePusher } from '@/utils/pusherUtils'; // Import Pusher


export default {
  props: {
    slug: { type: String, required: true }, // Accept slug as a prop
  },
  data() {
    return {
      bidAmount: "",
      message: null,
      isFavorite: false,
      loading: false,
      currentBid: null, // Store current bid value
      showModal: true, // Initial state to show the modal
      minimumBid: null, // Store minimum bid value
      selectedImage: null,
      product: null, // Store product details
      isLoadingProduct: true, // Loading state for product details
      isLoadingBid: true, // Loading state for bid details
      myStatus: null, // Store my_status message
      winStatus: null, // Store win status
      current_winner: null,
      showBidConfirmation: false, // Add bid confirmation modal state
      bidConfirmItem: null, // Add item data for confirmation
    };
  },
  mounted() {
    this.fetchProductDetails(); // Fetch product details when the component is mounted
    this.initializePusher(); // Initialize Pusher when the component is mounted
  },
  methods: {
    async fetchProductDetails() {
      const slug = this.slug;  // Use the passed slug

      if (!slug) {
        this.message = { text: "Product slug is missing.", type: "error" };
        this.isLoadingProduct = false;
        return;
      }

      const url = `/lots/detail?s=${slug}`;  // Use the slug as the alias to fetch product details
      this.isLoadingProduct = true; // Set loading state to true before starting fetch

      try {
        const response = await apiClient.get(url);
        if (response.data && response.data.data) {
          this.product = response.data.data;

          // Map image paths if images are present
          if (this.product.images && Array.isArray(this.product.images) && this.product.images.length > 0) {
            this.product.images = this.product.images.map(image => {
              image.path = image.path ? `storage/${image.path}` : null; // Assuming images are under /storage/
              return image;
            });

            // Set the first image as the default selected image if available
            this.selectedImage = this.product.images[0] || { path: null };
          } else {
            // No images found, set a fallback image
            this.selectedImage = { path: null };
          }


          // Set currentBid from wining_amount
          this.currentBid = this.product.wining_amount || 0;

          // Now that the product is fetched, fetch bid details
          this.fetchBidDetails();
        } else {
          throw new Error("No product data found.");
        }
      } catch (error) {
        this.message = { text: error.message || "Failed to fetch product details", type: "error" };
        this.selectedImage = { path: null }; // Fallback image
      } finally {
        this.isLoadingProduct = false; // Set loading state to false after fetch operations
      }
    },
    async fetchBidDetails() {
      if (!this.product || !this.product.id) {

        this.minimumBid = null;
        this.isLoadingBid = false;
        return;
      }

      const token = Cookies.get('token'); // Get the token from cookies
      if (!token) {
        this.minimumBid = null;
        this.myStatus = null;
        this.isLoadingBid = false;
        return;
      }

      this.isLoadingBid = true; // Set loading state to true before starting fetch

      try {
        const response = await apiClient.get(`/bid-details?lot_id=${this.product.id}`, {
          headers: {
            Authorization: `Bearer ${token}`, // Include the token in the request headers
          },
        }); // Fetch bid details using the product ID
        if (response.data) {
          this.minimumBid = response.data.minimum_bid || 0;
          this.myStatus = response.data.my_status || null; // Map my_status from bid details
          this.current_winner = response.data.uid;

          this.winStatus = response.data.winstatus || null; // Set win status
        } 
      } finally {
        this.isLoadingBid = false; // Set loading state to false after fetch operations
      }
    },
    async submitBid() {
      // Clear previous messages
      this.message = null;

      // Ensure the bid amount is a valid number and not empty
      const bid = parseFloat(this.bidAmount);

      // Check if the bid amount is not filled or is invalid
      if (!this.bidAmount || isNaN(bid) || bid <= 0) {
        this.message = {
          text: "Please enter a valid bid amount.",
          type: "error",
        };
        this.autoHideMessage();
        return;
      }

      // Check if the bid is less than the minimum allowed bid
      if (bid < this.minimumBid) {
        this.message = {
          text: `Please enter a bid of at least $${this.minimumBid}.`,
          type: "error",
        };
        this.autoHideMessage();
        return;
      }

      // Check for authentication token using Cookies
      const token = Cookies.get('token');
      if (!token) {
        this.message = { text: "You are not authenticated. Please log in.", type: "error" };
        this.autoHideMessage();
        setTimeout(() => {
          this.$router.push({ name: "LoginUser" });
        }, 1000); // Wait before redirecting
        return;
      }

      // Show confirmation popup instead of directly placing bid
      this.bidConfirmItem = { ...this.product, bidAmount: this.bidAmount };
      this.showBidConfirmation = true;
    },
    async confirmBid() {
      const bid = parseFloat(this.bidConfirmItem.bidAmount);
      const token = Cookies.get('token');
      this.showBidConfirmation = false;
      const confirmItem = this.bidConfirmItem;
      this.bidConfirmItem = null;
      
      this.loading = true;

      try {
        // Send the bid data to the backend
        const response = await apiClient.post(
          "/bids",
          {
            auction_id: this.product.auction_id,
            lot_id: this.product.id,
            bid_amount: bid,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Handle successful response
        if (response.data.success) {
          if (response.data.info) {
            this.message = { text: response.data.info, type: "info" };
          } else {
            this.message = { text: response.data.message, type: "success" };
          }
          this.currentBid = response.data.data.bid.bid_amount; // Update the current bid with the placed bid amount
          this.minimumBid = response.data.data.minimum_acceptable_bid; // Update the minimum acceptable bid
          this.myStatus = response.data.data.my_status; // Update myStatus with the response message
          this.bidAmount = ""; // Clear the bid amount field
          this.winStatus = response.data.data.winstatus; // Update winStatus with the response message
          this.current_winner = response.data?.bid?.uid;
        } else {
          // Handle failure response
          this.message = { text: response.data.message, type: "error" };
        }
      } catch (error) {
        console.error("Error placing bid:", error);

        // Handle specific error responses
        if (error.response && error.response.data && error.response.data.error) {
          this.message = { text: error.response.data.error, type: "error" };
        } else if (error.message) {
          this.message = { text: error.message, type: "error" };
        } else {
          this.message = { text: "An error occurred. Please try again.", type: "error" };
        }
      } finally {
        // Reset loading state
        this.loading = false;
      }
      this.autoHideMessage();
    },
    declineBid() {
      this.showBidConfirmation = false;
      this.bidConfirmItem = null;
    },
    autoHideMessage() {
      setTimeout(() => {
        this.message = null;
      }, 15000);
    },
    // Select an image
    selectImage(image) {
      this.selectedImage = image;
    },
    handleOverlayClick() {
      this.showModal = false; // Close modal when overlay is clicked
      document.body.style.overflow = ''; // Enable body scroll
      this.$emit("close"); // Optionally, emit close event to parent component
    },
    redirectToProductPage() {
      try {
        const slug = decodeURIComponent(this.product.slug); // Decode the slug
        const path = `/lot/${slug}`; // Construct the path manually
        window.location.href = path; // Use direct navigation to avoid encoding
      } catch (error) {
        console.error('Error decoding slug or navigating:', error);
      }
    },
    initializePusher() {
      initializePusher('my-channel', 'my-event', this.updateBidDetails);
    },
    updateBidDetails(message) {
      if (this.product && this.product.id == message.bid.lot_id) {
        this.currentBid = message.bid.bid_amount;
        this.minimumBid = message.minimum_acceptable_bid;

        if (Cookies.get('uid') == message.bid.uid && message.bid.uid > 0 ) {
          this.winStatus = 'winning';
          this.myStatus = message.winmsg;
        }

        if (Cookies.get('uid') == message.puid && message.puid > 0 ) {
          this.winStatus = 'loosing';
          this.myStatus = message.loosemsg;
        }
        this.current_winner = message.bid.uid;
      }
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'; // Disable body scroll when modal is open
      } else {
        document.body.style.overflow = ''; // Enable body scroll when modal is closed
      }
    }
  },
  components: {
    LoadingSpinner,
    BidConfirmation,
  },
};
</script>